import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseAccountModal } from './CloseAccountModal';
import { DeleteAccountModal } from './DeleteAccountModal';
import { Asset, ClickAnchor, ClickMenuContainer, ClickMenuItem, Icon, IconButton, Loan } from '../..';

type Account = Asset | Loan;

export interface EditAccountDetailsProps {
  account?: Account | null;
  clientID: string;
  householdID: string;
  onUpdateValue: () => void;
  isClosed?: boolean;
  onAccountMutationCompleted?: (action: 'closed' | 'deleted', account: Account) => void;
}

const EditAccountDetails: FC<EditAccountDetailsProps> = ({
  account,
  householdID,
  onUpdateValue,
  isClosed,
  onAccountMutationCompleted,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tAccountSideover } = useTranslation('accountSideover');
  const [showDeletionConfirmationModal, setShowDeletionConfirmationModal] = useState(false);
  const [showCloseConfirmationModal, setShowCloseConfirmationModal] = useState(false);

  const accountType = account?.__typename;

  return (
    <>
      <div className="fixed top-6 sm:top-8 right-6 sm:right-12 z-[11]">
        <ClickAnchor
          offset={[0, 8]}
          placement="bottom-end"
          referenceElementDataTestId="edit-account-details-button"
          floatingElement={
            <ClickMenuContainer className="!w-[267px] text-white">
              {!isClosed && (
                <ClickMenuItem hideOnClick className="text-lg" onClick={onUpdateValue}>
                  {tAccountSideover('update-value')}
                  <Icon className="text-palette-600" icon="addCircle" />
                </ClickMenuItem>
              )}
              {!isClosed && (
                <ClickMenuItem to={`edit/${accountType}`} hideOnClick className="text-lg">
                  {tAccountSideover('edit-details')}
                  <Icon className="text-palette-600" icon="create" />
                </ClickMenuItem>
              )}
              {!isClosed && (
                <ClickMenuItem hideOnClick to={'merge'} className="text-lg">
                  {tAccountSideover('merge')}
                  <Icon className="text-palette-600" icon="merge" />
                </ClickMenuItem>
              )}
              {!isClosed && (
                <ClickMenuItem
                  hideOnClick
                  className="text-lg"
                  onClick={() => setShowCloseConfirmationModal(true)}
                >
                  {tAccountSideover('close-account')}
                  <Icon className="text-darkMode-gray-light" icon="archive" />
                </ClickMenuItem>
              )}
              <ClickMenuItem
                hideOnClick
                className="text-lg"
                aria-label={tCommon('delete')}
                aria-role="button"
                onClick={() => setShowDeletionConfirmationModal(true)}
              >
                {tCommon('delete')}
                <Icon className="text-darkMode-danger" icon="delete" />
              </ClickMenuItem>
            </ClickMenuContainer>
          }
        >
          <IconButton theme="primary" icon="create" diameter={60} className="shadow-base" />
        </ClickAnchor>
      </div>
      {showCloseConfirmationModal && account ? (
        <CloseAccountModal
          account={account}
          householdID={householdID}
          show={showCloseConfirmationModal}
          onClose={() => setShowCloseConfirmationModal(false)}
          onAccountClosed={(closedAccount) => onAccountMutationCompleted?.('closed', closedAccount)}
        />
      ) : null}
      {showDeletionConfirmationModal && account ? (
        <DeleteAccountModal
          account={account}
          householdID={householdID}
          show={showDeletionConfirmationModal}
          onClose={() => setShowDeletionConfirmationModal(false)}
          onAccountDeleted={(deletedAccount) => onAccountMutationCompleted?.('deleted', deletedAccount)}
        />
      ) : null}
    </>
  );
};

export default EditAccountDetails;
